import type { EditorState, NodeHelpers } from "./types";

const ROOT_NODE_ID = "ROOT";

export function createNodeHelpers(
  state: EditorState,
  nodeId: string,
): NodeHelpers {
  return {
    get() {
      const node = state.nodes[nodeId];
      if (!node) {
        throw new Error(`Node ${nodeId} not found`);
      }
      return node;
    },

    ancestors() {
      const result: string[] = [];
      let current = state.nodes[nodeId];
      while (current?.data.parent) {
        result.push(current.data.parent);
        current = state.nodes[current.data.parent];
      }
      return result;
    },

    childNodes() {
      const node = state.nodes[nodeId];
      return node?.data.nodes ?? [];
    },

    isRoot() {
      return nodeId === ROOT_NODE_ID;
    },

    isDeletable() {
      return nodeId !== ROOT_NODE_ID && !!state.nodes[nodeId];
    },

    isCanvas() {
      return state.nodes[nodeId]?.data.isCanvas ?? false;
    },
  };
}

export function getDescendants(
  state: EditorState,
  nodeId: string,
): string[] {
  const result: string[] = [];
  const node = state.nodes[nodeId];
  if (!node) return result;

  const stack: string[] = [];
  const rootLinked = Object.values(node.data.linkedNodes);
  for (let i = rootLinked.length - 1; i >= 0; i -= 1) {
    stack.push(rootLinked[i]);
  }
  for (let i = node.data.nodes.length - 1; i >= 0; i -= 1) {
    stack.push(node.data.nodes[i]);
  }

  const seen = new Set<string>();
  while (stack.length > 0) {
    const currentId = stack.pop();
    if (!currentId || seen.has(currentId)) continue;
    seen.add(currentId);
    result.push(currentId);

    const current = state.nodes[currentId];
    if (!current) continue;

    const linkedIds = Object.values(current.data.linkedNodes);
    for (let i = linkedIds.length - 1; i >= 0; i -= 1) {
      stack.push(linkedIds[i]);
    }
    for (let i = current.data.nodes.length - 1; i >= 0; i -= 1) {
      stack.push(current.data.nodes[i]);
    }
  }
  return result;
}
